/**
 * /w/:slug/app/monitoring/configuration-items/:itemId(?system=) — V113-7-6, the configuration item page
 * (drill-in from the system Monitoring tab; ux-adversary run v113-monitoring, round 4).
 *
 *   Identity      name, human tag, owner, environment, tags (edit; environment needs an editor).
 *   Attributed    each system with its source, or "Manually attributed, last reviewed <date>" + Re-review.
 *   Methods       state (Identified › Verified › Mapped), fit, "Last verified", Verify. A FAILING method leads
 *                 with the fix: observed vs expected and its cause, Open finding (primary), Run all checks now.
 *   More          Retire · Merge (same kind only, required, past results stay) · Out of scope (reason required;
 *                 a failing item needs an owner / admin). A gone item offers Retire or relink with a suggested
 *                 successor. Provider ids sit behind Verify. Retired / merged items render read-only.
 * Role gates mirror monitoring-capabilities.ts; viewers see disabled controls with who to ask.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Alert, Button, Card, EmptyState, Field, Input, PageHeader, Select, Spinner, StatusGlyph, Textarea } from '@aigovops/design-system';
import type { CheckObservation, MonitoringMethod } from '@aigovops/core';
import { useWorkspace } from '../lib/workspace';
import {
  monitoringKeys, useConfigurationItem, useConfigurationItems, useLinkConfigurationItem, useMergeConfigurationItem, useMonitoringCapabilities,
  useMonitoringSummary, usePackValidity, useRetireConfigurationItem, useSetConfigurationItemScope, useSystemMonitoring,
  useUpdateConfigurationItem, useVerifyMethod,
} from '../hooks/useMonitoring';
import { monitoringErrorOf, type CiEnvironment, type ConfigurationItemRead, type SystemMonitoringView } from '../lib/monitoring-api';
import {
  askText, ciHref, detectorLabel, envLabel, findingHref, fitLabel, kindLabel, metaLine, methodStateLabel, monitoringFindingsHref, providerLabel, scopeOutCapability,
  systemTabHref, viaLabel, CHANGES_ANCHOR,
} from '../lib/monitoring-view';
import { fmtDate, plural } from '../lib/format';
import { ActionDialog, useAnnounce, useFocusAfter } from '../components/today/shared';
import { cadenceLabel, CauseLine, GatedButton, Observation, StatusChip, useMemberNames, useRunAllChecks, VerifyDisclosure } from '../components/monitoring/parts';
import { StepSheet, type StepSheetInput } from '../components/monitoring/SystemMonitoringTab';
import './today.css';
import './program-screens.css';
import './monitoring.css';

type MethodHealth = 'failing' | 'waiting' | 'stale' | 'passing' | 'unbound';
const ENVS: CiEnvironment[] = ['prod', 'staging', 'dev', 'transient'];
const STEPS = ['identified', 'verified', 'mapped'] as const;

type ItemMethod = MonitoringMethod & { latest: CheckObservation | null };

/** Per method: which control it serves (the item's own bindings), and how it is doing there. */
function methodFacts(m: ItemMethod, item: ConfigurationItemRead, sys: SystemMonitoringView | null, sysId: string | null): { control: string | null; health: MethodHealth } {
  const b = item.bindings.find((x) => x.method_id === m.method_id && (!sysId || x.system_id === sysId)) ?? item.bindings.find((x) => x.method_id === m.method_id) ?? null;
  const acc = sys?.acceptances.find((a) => a.method_id === m.method_id && !a.retired_at) ?? null;
  const control = b?.control_id ?? acc?.control_id ?? null;
  if (!b) return { control, health: acc ? 'waiting' : 'unbound' };
  if (!b.last_check_id) return { control, health: 'waiting' };
  const cell = sys?.coverage.controls.find((c) => c.control_id === b.control_id)?.components.find((c) => c.component_id === m.component_id);
  if (cell?.reason === 'last_check_not_passing' || (!cell && b.latest?.status === 'fail')) return { control, health: 'failing' };
  if (cell?.reason === 'binding_not_fresh' || b.binding_status === 'stale') return { control, health: 'stale' };
  return { control, health: 'passing' };
}
const HEALTH_CHIP: Record<MethodHealth, { label: string; tone: 'red' | 'blue' | 'amber' | 'ok' | 'outline'; glyph: 'x' | 'dots' | 'clock' | 'check' | 'circle' }> = {
  failing: { label: 'Stopped passing', tone: 'outline', glyph: 'x' },
  waiting: { label: 'Waiting for first run', tone: 'blue', glyph: 'dots' },
  stale: { label: 'Past cadence', tone: 'amber', glyph: 'clock' },
  passing: { label: 'Passing', tone: 'ok', glyph: 'check' },
  unbound: { label: 'Not mapped to a control', tone: 'outline', glyph: 'circle' },
};

export function ConfigurationItemPage() {
  const { itemId = '' } = useParams<{ itemId: string }>();
  const [search] = useSearchParams();
  const { workspace } = useWorkspace();
  const slug = workspace.slug;
  const prefix = `/w/${slug}/app`;
  const q = useConfigurationItem(itemId);
  const item = q.data?.configuration_item ?? null;
  const sysId = search.get('system') && item?.systems.some((s) => s.system_id === search.get('system')) ? search.get('system')! : item?.systems[0]?.system_id ?? null;
  const sys = useSystemMonitoring(sysId);
  const caps = useMonitoringCapabilities();
  const [announcer, announce] = useAnnounce();
  const focusAfter = useFocusAfter();

  if (q.loading) return <div className="mon-page"><Spinner label="Loading configuration item" /></div>;
  if (q.error || !item) {
    const nf = q.error && /not found/i.test(q.error);
    return (
      <div className="mon-page">
        <PageHeader variant="app" title="Configuration item" headingId="ci-h1" />
        {nf || !q.error ? (
          <EmptyState title="This configuration item isn’t here" description="It may have been merged or removed."
            action={<Link className="gp-linkbtn" to={`${prefix}/monitoring`}>Back to Monitoring</Link>} />
        ) : <Alert variant="error">The configuration item could not be loaded. <Button compact onClick={() => void q.refetch()}>Retry</Button></Alert>}
      </div>
    );
  }

  const titles = new Map(item.bindings.filter((b) => b.control_title).map((b) => [b.control_id, b.control_title!]));
  const title = (id: string | null) => (id ? titles.get(id) ?? id : 'a control');
  const methods = item.methods.filter((m) => !m.retired_at).map((m) => ({ m, ...methodFacts(m, item, sys.data ?? null, sysId) }));
  const failing = methods.find((x) => x.health === 'failing') ?? null;
  const readOnly = !!item.retired_at || !!item.merged_into_id;
  const headChip = readOnly ? { label: item.retired_at ? 'Retired' : 'Merged', tone: 'outline' as const, glyph: 'circle' as const }
    : item.gone_at ? { label: 'Gone', tone: 'amber' as const, glyph: 'warning' as const }
      : failing ? HEALTH_CHIP.failing
        : methods.some((x) => x.health === 'waiting') ? HEALTH_CHIP.waiting
          : methods.some((x) => x.health === 'stale') ? HEALTH_CHIP.stale
            : methods.some((x) => x.health === 'passing') ? HEALTH_CHIP.passing
              : { label: 'Unmonitored', tone: 'outline' as const, glyph: 'circle' as const };
  const sysName = item.systems.find((s) => s.system_id === sysId)?.system_name ?? null;

  const methodsCard = <Methods key="methods" item={item} methods={methods} title={title} sys={sys.data ?? null} sysId={sysId} slug={slug} prefix={prefix} caps={caps} readOnly={readOnly} onAnnounce={announce} focusAfter={focusAfter} />;

  return (
    <div className="mon-page" data-part="configuration-item">
      {announcer}
      <p className="mon-crumbs">
        {sysId && sysName ? <Link to={systemTabHref(prefix, sysId, 'mon-items')}>{sysName}</Link> : <Link to={`${prefix}/monitoring`}>Monitoring</Link>} / Configuration items
      </p>
      <PageHeader variant="app" title={item.ci_name} headingId="ci-h1"
        subtitle={`${kindLabel(item.kind)} · ${envLabel(item.environment)} · seen by the ${providerLabel(item.verify.provider_key)} connector · ${fmtDate(item.last_seen_at)}`}
        right={<StatusChip label={headChip.label} tone={headChip.tone} glyph={headChip.glyph} large />} />
      {readOnly ? (
        <div id="ci-readonly" tabIndex={-1}><Alert variant="info">{item.retired_at ? `Retired ${fmtDate(item.retired_at)}. Past results stay readable and verifiable.` : 'Merged into another item. Past results stay readable and verifiable.'}
          {item.merged_into_id ? <> <Link to={ciHref(prefix, item.merged_into_id, sysId)}>Open the item it was merged into</Link></> : null}</Alert></div>
      ) : null}
      {item.gone_at && !readOnly ? <GoneCard item={item} slug={slug} prefix={prefix} sysId={sysId} caps={caps} onAnnounce={announce} focusAfter={focusAfter} /> : null}
      {failing ? methodsCard : null}
      <div className="mon-two">
        <Identity item={item} slug={slug} caps={caps} readOnly={readOnly} onAnnounce={announce} focusAfter={focusAfter} />
        <Attributed item={item} prefix={prefix} caps={caps} readOnly={readOnly} onAnnounce={announce} focusAfter={focusAfter} />
      </div>
      {!failing ? methodsCard : null}
      {!readOnly ? <More item={item} slug={slug} prefix={prefix} sysId={sysId} failing={!!failing} caps={caps} onAnnounce={announce} focusAfter={focusAfter} /> : null}
    </div>
  );
}

type Caps = ReturnType<typeof useMonitoringCapabilities>;
type Focus = (id: string) => void;

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

function Identity({ item, slug, caps, readOnly, onAnnounce, focusAfter }: { item: ConfigurationItemRead; slug: string; caps: Caps; readOnly: boolean; onAnnounce: (m: string) => void; focusAfter: Focus }) {
  const uid = useId();
  const names = useMemberNames(slug);
  const update = useUpdateConfigurationItem(item.component_id);
  const [editing, setEditing] = useState(false);
  const [f, setF] = useState({ name: '', tag: '', owner: '', env: '', tags: '' });
  const [error, setError] = useState<string | null>(null);
  const [nameErr, setNameErr] = useState<string | null>(null);
  const start = () => {
    setF({ name: item.ci_name, tag: item.ci_tag ?? '', owner: item.owner_user_id ?? '', env: item.environment ?? '', tags: item.tags.join(', ') });
    setError(null); setNameErr(null); setEditing(true); focusAfter(`${uid}-name`);
  };
  const owners = useMemo(() => {
    const list = [...names.entries()];
    if (item.owner_user_id && !names.has(item.owner_user_id)) list.unshift([item.owner_user_id, item.owner_name ?? 'Current owner']);
    return list;
  }, [names, item.owner_user_id, item.owner_name]);
  const envCap = caps.change_environment;
  return (
    <Card density="compact" headingId="ci-identity-h" title="Identity"
      right={!readOnly && !editing ? <GatedButton cap={caps.edit_identity} ask={askText(caps.edit_identity, { ownerName: item.owner_name })} compact onClick={start}>Edit</GatedButton> : undefined}>
      {editing ? (
        <form className="mon-col" noValidate onSubmit={async (e) => {
          e.preventDefault();
          if (!f.name.trim()) { setNameErr('A name is required.'); focusAfter(`${uid}-name`); return; }
          const tags = f.tags.split(',').map((t) => t.trim()).filter(Boolean);
          const patch = {
            ci_name: f.name.trim() !== item.ci_name ? f.name.trim() : undefined,
            ci_tag: (f.tag.trim() || null) !== item.ci_tag ? f.tag.trim() || null : undefined,
            owner_user_id: (f.owner || null) !== item.owner_user_id ? f.owner || null : undefined,
            environment: (f.env || null) !== item.environment ? ((f.env || null) as CiEnvironment | null) : undefined,
            tags: tags.join('\u0000') !== item.tags.join('\u0000') ? tags : undefined,
          };
          if (Object.values(patch).every((x) => x === undefined)) { setEditing(false); focusAfter('ci-identity-h'); return; }
          setError(null);
          try { await update.mutateAsync(patch); setEditing(false); onAnnounce('Identity saved'); focusAfter('ci-identity-h'); }
          catch (err) { const m = monitoringErrorOf(err); setError(m.kind === 'forbidden' ? 'Only an owner, admin or editor can change the environment.' : m.message || 'Could not save.'); }
        }}>
          {error ? <Alert variant="error">{error}</Alert> : null}
          <Field id={`${uid}-name`} label="Name" required error={nameErr ?? undefined}><Input value={f.name} onChange={(e) => { setF({ ...f, name: e.currentTarget.value }); setNameErr(null); }} /></Field>
          <Field id={`${uid}-tag`} label="Tag" hint="A name people use for it"><Input value={f.tag} onChange={(e) => setF({ ...f, tag: e.currentTarget.value })} /></Field>
          <Field id={`${uid}-owner`} label="Owner">
            <Select value={f.owner} onChange={(e) => setF({ ...f, owner: e.currentTarget.value })}>
              <option value="">No owner</option>
              {owners.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
            </Select>
          </Field>
          <Field id={`${uid}-env`} label="Environment" hint={envCap.allowed ? undefined : 'Ask an owner, admin or editor to change it'}>
            <Select value={f.env} disabled={!envCap.allowed} onChange={(e) => setF({ ...f, env: e.currentTarget.value })}>
              <option value="">Not set</option>
              {ENVS.map((x) => <option key={x} value={x}>{envLabel(x)}</option>)}
            </Select>
          </Field>
          <Field id={`${uid}-tags`} label="Tags" hint="Separate with commas"><Input value={f.tags} onChange={(e) => setF({ ...f, tags: e.currentTarget.value })} /></Field>
          <div className="mon-actions">
            <Button type="submit" variant="primary" aria-disabled={update.isPending || undefined}>{update.isPending ? 'Saving…' : 'Save'}</Button>
            <Button onClick={() => { setEditing(false); focusAfter('ci-identity-h'); }}>Cancel</Button>
          </div>
        </form>
      ) : (
        <dl className="mon-dl">
          <dt>Name</dt><dd>{item.ci_name}</dd>
          <dt>Tag</dt><dd>{item.ci_tag || '—'}</dd>
          <dt>Owner</dt><dd>{item.owner_name ?? (item.owner_user_id ? names.get(item.owner_user_id) : null) ?? 'No owner'}</dd>
          <dt>Environment</dt><dd>{envLabel(item.environment)}</dd>
          <dt>Tags</dt><dd>{item.tags.length ? <span className="mon-tags">{item.tags.map((t) => <StatusChip key={t} label={t} tone="muted" />)}</span> : '—'}</dd>
          <dt>First seen</dt><dd>{fmtDate(item.first_seen_at)}</dd>
        </dl>
      )}
      <VerifyDisclosure label="Verify · provider ids">
        <dl className="mon-dl">
          <dt>Provider</dt><dd>{item.verify.provider_name}</dd>
          <dt>Provider id</dt><dd className="mon-mono">{item.verify.external_id}</dd>
          <dt>Key</dt><dd className="mon-mono">{item.verify.component_key}</dd>
        </dl>
      </VerifyDisclosure>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Attributed systems
// ---------------------------------------------------------------------------

function Attributed({ item, prefix, caps, readOnly, onAnnounce, focusAfter }: { item: ConfigurationItemRead; prefix: string; caps: Caps; readOnly: boolean; onAnnounce: (m: string) => void; focusAfter: Focus }) {
  const uid = useId();
  const link = useLinkConfigurationItem(item.component_id);
  const summary = useMonitoringSummary();
  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [rowErr, setRowErr] = useState<string | null>(null);
  const others = (summary.data?.systems ?? []).filter((s) => !item.systems.some((x) => x.system_id === s.system_id));
  const now = Date.now();
  return (
    <Card density="compact" headingId="ci-attr-h" title="Attributed to"
      right={!readOnly ? <GatedButton cap={caps.link_system} ask={askText(caps.link_system, { ownerName: item.owner_name })} compact onClick={() => { setChoice(''); setError(null); setOpen(true); }}>Attribute to a system</GatedButton> : undefined}>
      {!item.systems.length ? <p className="mon-hint">Not attributed to a system yet, so it counts nowhere.</p> : (
        <ul className="mon-rows">
          {item.systems.map((s) => {
            const due = s.review_due_at && Date.parse(s.review_due_at) - now < 14 * 86_400_000;
            return (
              <li key={s.system_id} className="mon-row" id={`ci-sys-${s.system_id}`} tabIndex={-1}>
                <span className="mon-row-main">
                  <Link className="mon-name" to={systemTabHref(prefix, s.system_id)}>{s.system_name}</Link>
                  <span className="mon-sub">{s.via === 'manual' ? `Manually attributed, last reviewed ${fmtDate(s.attribution_reviewed_at)}` : viaLabel(s.via)}</span>
                </span>
                {s.via === 'manual' && !readOnly ? (
                  <span className="mon-row-acts">
                    {s.review_due_at ? <StatusChip label={`Re-review by ${fmtDate(s.review_due_at)}`} tone={due ? 'amber' : 'muted'} glyph="clock" /> : null}
                    <GatedButton cap={caps.link_system} ask={askText(caps.link_system, { ownerName: item.owner_name })} compact busy={link.isPending && link.variables === s.system_id}
                      onClick={async () => {
                        setRowErr(null);
                        try { await link.mutateAsync(s.system_id); onAnnounce(`Re-reviewed for ${s.system_name}`); focusAfter(`ci-sys-${s.system_id}`); }
                        catch (e) { setRowErr(monitoringErrorOf(e).message || 'Could not re-review.'); }
                      }}>Re-review</GatedButton>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {rowErr ? <p className="mon-err" role="alert">{rowErr}</p> : null}
      <ActionDialog open={open} title="Attribute to a system" submitLabel="Attribute" busy={link.isPending} error={error} onClose={() => setOpen(false)}
        onSubmit={async () => {
          if (!choice) { setError('Pick a system.'); return false; }
          try { await link.mutateAsync(choice); setOpen(false); onAnnounce('Attributed'); focusAfter(`ci-sys-${choice}`); return true; }
          catch (e) { setError(monitoringErrorOf(e).message || 'Could not attribute.'); return false; }
        }}>
        <Field id={`${uid}-sys`} label="System" required>
          <Select value={choice} onChange={(e) => setChoice(e.currentTarget.value)}>
            <option value="">Choose a system</option>
            {others.map((s) => <option key={s.system_id} value={s.system_id}>{s.name}</option>)}
          </Select>
        </Field>
        <p className="mon-hint">Manual attributions are re-reviewed on a cadence.</p>
      </ActionDialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Methods
// ---------------------------------------------------------------------------

function Methods({ item, methods, title, sys, sysId, slug, prefix, caps, readOnly, onAnnounce, focusAfter }: {
  item: ConfigurationItemRead; methods: Array<{ m: ItemMethod; control: string | null; health: MethodHealth }>; title: (id: string | null) => string;
  sys: SystemMonitoringView | null; sysId: string | null; slug: string; prefix: string; caps: Caps; readOnly: boolean; onAnnounce: (m: string) => void; focusAfter: Focus;
}) {
  const verify = useVerifyMethod(item.component_id);
  const validity = usePackValidity();
  const names = useMemberNames(slug);
  const [err, setErr] = useState<string | null>(null);
  const runAll = useRunAllChecks(slug);
  const [step, setStep] = useState<StepSheetInput | null>(null);
  const validityCause = (validity.data?.validity?.reasons ?? []).flatMap((r) => r.items).find((i) => i.system_id === sysId && i.cause)?.cause?.label ?? null;
  const next = sys?.facts.next_action ?? null;
  const changesHref = sysId ? systemTabHref(prefix, sysId, CHANGES_ANCHOR) : null;
  return (
    <Card density="compact" id="ci-methods" tabIndex={-1} headingId="ci-methods-h" title="Methods" right={<p className="mon-card-note">Only verified, mapped methods count</p>}>
      {err ? <Alert variant="error">{err}</Alert> : null}
      {runAll.error ? <Alert variant="error">{runAll.error}</Alert> : null}
      {runAll.toast}
      {!methods.length ? (
        <EmptyState title="No method yet" description="Nothing checks this item, so its controls can’t pass."
          action={sysId ? <Link className="gp-linkbtn" to={systemTabHref(prefix, sysId, 'mon-candidates')}>See tools that can cover it</Link> : <Link className="gp-linkbtn" to={`${prefix}/setup`}>Connect a source</Link>} />
      ) : (
        <ul className="mon-rows">
          {methods.map(({ m, control, health }) => {
            const chip = HEALTH_CHIP[health];
            const findingId = health === 'failing' && next?.target.control_id === control ? next?.target.finding_id ?? null : null;
            const stepAt = STEPS.indexOf(m.state as (typeof STEPS)[number]);
            const by = m.verified_by ? names.get(m.verified_by) ?? null : null;
            const sysMethod = sys?.methods.find((x) => x.method_id === m.method_id) ?? null;
            const pipelineStep = health === 'stale' && m.detector_kind === 'ci_parser' && sysMethod && (sysMethod.setup || sysMethod.setup_unavailable_reason) ? sysMethod : null;
            // A failing finding with no id in hand still opens Findings filtered to the control.
            const findingTo = findingId ? findingHref(prefix, findingId) : monitoringFindingsHref(prefix, { system: sysId, control });
            return (
              <li key={m.method_id} className={`mon-row${health === 'failing' ? ' mon-row--block' : ''}`} id={`ci-m-${m.method_id}`} tabIndex={-1}>
                <span className="mon-row-main">
                  <span className="mon-name">{detectorLabel(m.detector_kind, m.detector_ref)}</span>
                  <span className="mon-line">
                    <span>{providerLabel(item.verify.provider_key)} {m.detector_kind === 'attestation' ? 'attestation' : 'check'}</span>
                    <strong>{fitLabel(m.fit)}</strong>
                    {control ? <span>For {title(control)}</span> : null}
                    <span>{m.verified_at ? `Last verified ${fmtDate(m.verified_at)}${by ? ` by ${by}` : ''}` : 'Not verified yet'}</span>
                    {m.cadence_seconds ? <span>{cadenceLabel(m.cadence_seconds)}</span> : null}
                  </span>
                  {health === 'failing' ? (
                    <div className="mon-fix mon-fix--red" data-part="fix">
                      <div className="mon-fix-head">
                        <Observation latest={m.latest} context={control ? title(control) : detectorLabel(m.detector_kind, m.detector_ref)} />
                        {readOnly ? null : (
                          <span className="mon-actions">
                            <Link className="gp-linkbtn gp-linkbtn--compact btn-primary" to={findingTo}>Open finding</Link>
                            <Button compact aria-disabled={runAll.busy || undefined} onClick={() => void runAll.run()}>Run all checks now</Button>
                          </span>
                        )}
                      </div>
                      <span className="mon-sub">{m.latest ? `Checked ${fmtDate(m.latest.observed_at)}` : 'Last check failed'}{sys?.facts.sla_due ? ` · Due ${fmtDate(sys.facts.sla_due)}` : ''}</span>
                      {m.latest?.cause ? <CauseLine cause={m.latest.cause} seeHref={changesHref} />
                        : validityCause ? <span className="mon-sub" data-part="cause">{validityCause}</span> : null}
                    </div>
                  ) : null}
                  <ol className="mon-steps" aria-label="Method state">
                    {STEPS.map((s, i) => (
                      <li key={s} aria-current={i === stepAt ? 'step' : undefined}>
                        <StatusGlyph kind={i <= stepAt ? 'check' : 'circle'} size={12} />{methodStateLabel(s)}{i < STEPS.length - 1 ? <span aria-hidden="true"> ›</span> : null}
                      </li>
                    ))}
                  </ol>
                  <VerifyDisclosure label="Details" context={detectorLabel(m.detector_kind, m.detector_ref)}>
                    <dl className="mon-dl">
                      <dt>Method</dt><dd className="mon-mono">{m.method_id}</dd>
                      <dt>Detector</dt><dd className="mon-mono">{m.detector_kind}:{m.detector_ref}</dd>
                      <dt>Expires</dt><dd>{fmtDate(m.expires_at)}</dd>
                    </dl>
                  </VerifyDisclosure>
                </span>
                {health === 'failing' ? null : (
                <span className="mon-row-acts">
                  <StatusChip label={chip.label} tone={chip.tone} glyph={chip.glyph} />
                  {readOnly ? null : pipelineStep ? (
                    <Button compact onClick={() => setStep({
                      name: detectorLabel(m.detector_kind, m.detector_ref), controlTitle: control ? title(control) : '—', componentName: item.ci_name,
                      setup: pipelineStep.setup, unavailable: pipelineStep.setup_unavailable_reason,
                    })}>Show the step</Button>
                  ) : (
                    <GatedButton cap={caps.verify_method} ask={askText(caps.verify_method, { ownerName: item.owner_name })} compact busy={verify.isPending && verify.variables === m.method_id}
                      onClick={async () => {
                        setErr(null);
                        try { await verify.mutateAsync(m.method_id); onAnnounce(`${detectorLabel(m.detector_kind, m.detector_ref)} verified`); focusAfter(`ci-m-${m.method_id}`); }
                        catch (e) { setErr(monitoringErrorOf(e).message || 'Could not verify.'); }
                      }}>{m.verified_at ? 'Verify again' : 'Verify'}</GatedButton>
                  )}
                </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <StepSheet input={step} prefix={prefix} onClose={() => setStep(null)} />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Gone → retire or relink; More: retire, merge, out of scope
// ---------------------------------------------------------------------------

function useSameKind(item: ConfigurationItemRead) {
  const all = useConfigurationItems();
  return useMemo(() => (all.data?.configuration_items ?? [])
    .filter((c) => c.component_id !== item.component_id && c.kind === item.kind && !c.retired_at && !c.merged_into_id && !c.gone_at)
    .sort((a, b) => Date.parse(b.first_seen_at) - Date.parse(a.first_seen_at)), [all.data, item.component_id, item.kind]);
}

function GoneCard({ item, slug, prefix, sysId, caps, onAnnounce, focusAfter }: { item: ConfigurationItemRead; slug: string; prefix: string; sysId: string | null; caps: Caps; onAnnounce: (m: string) => void; focusAfter: Focus }) {
  const same = useSameKind(item);
  const successor = same.find((c) => item.gone_at && Date.parse(c.first_seen_at) >= Date.parse(item.gone_at) - 7 * 86_400_000) ?? same[0] ?? null;
  const [merge, setMerge] = useState(false);
  const [retire, setRetire] = useState(false);
  return (
    <Card dashed density="compact" ariaLabel="No longer seen">
      <h2 id="ci-gone-h" className="mon-next-title">No longer seen since {fmtDate(item.gone_at)}</h2>
      <p className="mon-hint">{successor ? <>Suggested successor: <strong>{successor.ci_name}</strong> · first seen {fmtDate(successor.first_seen_at)}</> : 'No same-kind item to relink to.'}</p>
      <div className="mon-actions">
        {successor ? <GatedButton cap={caps.merge} ask="Ask an owner or admin" variant="primary" onClick={() => setMerge(true)}>Relink to {successor.ci_name}</GatedButton> : null}
        <GatedButton cap={caps.retire} ask="Ask an owner or admin" onClick={() => setRetire(true)}>Retire</GatedButton>
      </div>
      <MergeDialog open={merge} item={item} slug={slug} prefix={prefix} sysId={sysId} preselect={successor?.component_id ?? ''} onClose={() => setMerge(false)} onAnnounce={onAnnounce} />
      <RetireDialog open={retire} item={item} onClose={() => setRetire(false)} onAnnounce={onAnnounce} onRetired={() => focusAfter('ci-readonly')} />
    </Card>
  );
}

function MergeDialog({ open, item, slug, prefix, sysId, preselect, onClose, onAnnounce }: {
  open: boolean; item: ConfigurationItemRead; slug: string; prefix: string; sysId: string | null; preselect?: string; onClose: () => void; onAnnounce: (m: string) => void;
}) {
  const uid = useId();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const merge = useMergeConfigurationItem(item.component_id);
  const same = useSameKind(item);
  const [into, setInto] = useState('');
  const [fieldErr, setFieldErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) { setInto(preselect ?? ''); setFieldErr(null); setError(null); } }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const target = same.find((c) => c.component_id === into) ?? null;
  return (
    <ActionDialog open={open} title={`Merge ${item.ci_name} into another ${kindLabel(item.kind).toLowerCase()}`} submitLabel={target ? `Merge into ${target.ci_name}` : 'Merge'}
      busy={merge.isPending} error={error} onClose={onClose}
      onSubmit={async () => {
        if (!into) { setFieldErr('Pick the item to keep.'); return false; }
        try {
          const out = await merge.mutateAsync(into);
          await qc.invalidateQueries({ queryKey: monitoringKeys.root(slug) });
          onAnnounce(`Merged into ${target?.ci_name || 'the kept item'} · past results stay`);
          onClose();
          navigate(ciHref(prefix, out.merge.survivor_id, sysId));
          return true;
        } catch (e) { setError(monitoringErrorOf(e).message || 'Could not merge.'); return false; }
      }}>
      {!same.length ? <p className="mon-hint">No other {kindLabel(item.kind).toLowerCase()} to merge into.</p> : (
        <Field id={`${uid}-into`} label="Keep" required error={fieldErr ?? undefined}>
          <Select value={into} onChange={(e) => { setInto(e.currentTarget.value); setFieldErr(null); }}>
            <option value="">Choose the item to keep</option>
            {same.map((c) => <option key={c.component_id} value={c.component_id}>{c.ci_name} · first seen {fmtDate(c.first_seen_at)}</option>)}
          </Select>
        </Field>
      )}
      <p className="mon-hint">{item.ci_name} is removed · past results stay on it (read-only, still verifiable). Live bindings move to the kept item.</p>
    </ActionDialog>
  );
}

function RetireDialog({ open, item, onClose, onAnnounce, onRetired }: { open: boolean; item: ConfigurationItemRead; onClose: () => void; onAnnounce: (m: string) => void; onRetired?: () => void }) {
  const retire = useRetireConfigurationItem(item.component_id);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => { if (open) setError(null); }, [open]);
  return (
    <ActionDialog open={open} title={`Retire ${item.ci_name}`} submitLabel="Retire" danger busy={retire.isPending} error={error} onClose={onClose}
      onSubmit={async () => {
        try { await retire.mutateAsync(undefined); onAnnounce('Retired · past results stay readable'); onClose(); onRetired?.(); return true; }
        catch (e) { const f = monitoringErrorOf(e); setError(f.kind === 'conflict' ? 'It is in an assessed category during fieldwork; it can be retired after fieldwork.' : f.message || 'Could not retire.'); return false; }
      }}>
      <p className="mon-hint">It stops counting toward any control. Past results stay readable and verifiable.</p>
    </ActionDialog>
  );
}

function More({ item, slug, prefix, sysId, failing, caps, onAnnounce, focusAfter }: {
  item: ConfigurationItemRead; slug: string; prefix: string; sysId: string | null; failing: boolean; caps: Caps; onAnnounce: (m: string) => void; focusAfter: Focus;
}) {
  const uid = useId();
  const scope = useSetConfigurationItemScope(item.component_id);
  const sysQ = useSystemMonitoring(sysId);
  const [merge, setMerge] = useState(false);
  const [retire, setRetire] = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [scopeSys, setScopeSys] = useState('');
  const [reason, setReason] = useState('');
  const [reasonErr, setReasonErr] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const outFor = (sysQ.data?.coverage.buckets.out_of_scope ?? []).find((x) => x.component_id === item.component_id) ?? null;
  const { role } = useWorkspace();
  const scopeCap = scopeOutCapability({ role }, failing);
  const inCap = caps.set_scope;
  return (
    <Card density="compact" headingId="ci-more-h" title="Retire, merge or scope" right={<p className="mon-card-note">Recorded in the audit trail</p>}>
        <div className="mon-col">
          {outFor ? (
            <p className="mon-line mon-line--text"><StatusChip label="Out of scope" tone="outline" glyph="circle" />
              <span>{metaLine([outFor.system_name, outFor.reason, outFor.decided_by, outFor.decided_at ? fmtDate(outFor.decided_at) : null])}</span></p>
          ) : null}
          {error ? <Alert variant="error">{error}</Alert> : null}
          <div className="mon-actions mon-actions--stack">
            <GatedButton cap={caps.merge} ask="Ask an owner or admin" onClick={() => setMerge(true)}>Merge into another item</GatedButton>
            {outFor ? (
              <GatedButton cap={inCap} ask={askText(inCap, { ownerName: item.owner_name })} busy={scope.isPending} onClick={async () => {
                setError(null);
                try { await scope.mutateAsync({ systemId: outFor.system_id, scope: 'in_scope' }); onAnnounce('Back in scope'); focusAfter('ci-more-h'); }
                catch (e) { setError(monitoringErrorOf(e).message || 'Could not bring it into scope.'); }
              }}>Bring into scope</GatedButton>
            ) : item.systems.length ? (
              <GatedButton cap={scopeCap} ask={askText(scopeCap, { ownerName: item.owner_name, adminOnly: failing })} onClick={() => { setScopeSys(sysId ?? item.systems[0]!.system_id); setReason(''); setReasonErr(null); setError(null); setScopeOpen(true); }}>Mark out of scope</GatedButton>
            ) : null}
            <GatedButton cap={caps.retire} ask="Ask an owner or admin" variant="danger" onClick={() => setRetire(true)}>Retire this item</GatedButton>
          </div>
        </div>
      <MergeDialog open={merge} item={item} slug={slug} prefix={prefix} sysId={sysId} onClose={() => setMerge(false)} onAnnounce={onAnnounce} />
      <RetireDialog open={retire} item={item} onClose={() => setRetire(false)} onAnnounce={onAnnounce} onRetired={() => focusAfter('ci-readonly')} />
      <ActionDialog open={scopeOpen} title={`Mark ${item.ci_name} out of scope`} submitLabel="Mark out of scope" busy={scope.isPending} error={error} onClose={() => setScopeOpen(false)}
        subtitle={failing ? 'Its check is failing: the pack goes stale until this is reviewed.' : undefined}
        onSubmit={async () => {
          if (!reason.trim()) { setReasonErr('Say why it is out of scope.'); setTimeout(() => document.getElementById(`${uid}-r`)?.focus(), 0); return false; }
          try { await scope.mutateAsync({ systemId: scopeSys, scope: 'out_of_scope', reason: reason.trim() }); setScopeOpen(false); onAnnounce('Marked out of scope'); focusAfter('ci-more-h'); return true; }
          catch (e) {
            const f = monitoringErrorOf(e);
            if (f.kind === 'reason_required') { setReasonErr('Say why it is out of scope.'); setTimeout(() => document.getElementById(`${uid}-r`)?.focus(), 0); return false; }
            setError(f.kind === 'forbidden' ? 'Its check is failing; only an owner or admin can take it out of scope.' : f.message || 'Could not change scope.');
            return false;
          }
        }}>
        {item.systems.length > 1 ? (
          <Field id={`${uid}-s`} label="For system" required>
            <Select value={scopeSys} onChange={(e) => setScopeSys(e.currentTarget.value)}>
              {item.systems.map((s) => <option key={s.system_id} value={s.system_id}>{s.system_name}</option>)}
            </Select>
          </Field>
        ) : null}
        <Field id={`${uid}-r`} label="Reason" required error={reasonErr ?? undefined}>
          <Textarea rows={3} value={reason} placeholder="e.g. SSO app only; no model traffic" onChange={(e) => { setReason(e.currentTarget.value); setReasonErr(null); }} />
        </Field>
        <p className="mon-hint">Shown on the shared status with the date. {plural(item.systems.length, 'system')} linked.</p>
      </ActionDialog>
    </Card>
  );
}
